import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { ArrowLeft, Check, Upload, AlertCircle, DollarSign } from 'lucide-react';

const PaymentSummaryPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [licenseData, setLicenseData] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [proofFile, setProofFile] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      const response = await api.get('/api/license/current-pricing');
      setLicenseData(response.data);
      
      // Si ya tiene billing_cycle, es renovación
      if (response.data.billing_cycle) {
        setBillingCycle(response.data.billing_cycle);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const calculateAmount = () => {
    const pricingConfig = licenseData?.pricing_config;
    const packageKey = licenseData?.package_key || 'free';
    
    // Obtener precios del paquete según ciclo
    const planPrices = pricingConfig?.[packageKey] || {};
    let packagePrice = planPrices[billingCycle] || 0;
    
    // Para mensual, agregar routers extra
    // Para otros ciclos, el precio YA está en la BD
    let extraRoutersCost = 0;
    const totalRouters = licenseData?.routers_count || 0;
    const baseRouters = packageKey === 'todo_incluido' ? 999 : 1;
    const extraRouters = Math.max(0, totalRouters - baseRouters);
    
    if (billingCycle === 'monthly' && extraRouters > 0) {
      const extraRouterPrice = pricingConfig?.extra_router_cost || 50;
      extraRoutersCost = extraRouters * extraRouterPrice;
    }
    
    const total = packagePrice + extraRoutersCost;
    
    // Calcular ahorro (comparado con pagar mensual)
    const monthlyPrice = planPrices['monthly'] || 0;
    let saving = 0;
    if (billingCycle === 'semiannual') {
      saving = (monthlyPrice * 6) - packagePrice;
    } else if (billingCycle === 'annual') {
      saving = (monthlyPrice * 12) - packagePrice;
    } else if (billingCycle === 'lifetime') {
      saving = (monthlyPrice * 12) - packagePrice;
    }
    
    return {
      packagePrice,
      extraRoutersCost,
      extraRouters,
      total,
      saving: Math.max(0, saving)
    };
  };
  
  const handleSubmit = async () => {
    if (!proofFile) {
      alert('Por favor selecciona un comprobante de pago');
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Usar FormData para enviar archivo (igual que herramientas)
      const formData = new FormData();
      formData.append('billing_cycle', billingCycle);
      formData.append('notes', notes || '');
      formData.append('receipt', proofFile);
      
      // Endpoint con upload de archivo
      const endpoint = licenseData?.billing_cycle 
        ? '/api/payment-requests/create-renewal-with-file'
        : '/api/payment-requests/create-first-payment-with-file';
      
      await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      alert('✅ Solicitud enviada. Tu cuenta quedará en revisión hasta que el administrador apruebe tu pago.');
      navigate('/dashboard');
      
    } catch (error) {
      alert(error.response?.data?.detail || 'Error enviando solicitud');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  const amounts = calculateAmount();
  const packageName = licenseData?.package_name || 'FREE';
  const pricingConfig = licenseData?.pricing_config;
  
  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} p-6`}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {licenseData?.billing_cycle 
              ? '💳 Renovación de Plan' 
              : '🎉 Tu período de prueba terminó'}
          </h1>
          <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {licenseData?.billing_cycle
              ? 'Sube tu comprobante de pago para renovar tu acceso'
              : 'Elige cómo pagar para continuar usando el sistema'}
          </p>
        </div>
        
        {/* Resumen de Uso */}
        <div className={`rounded-xl p-6 mb-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            📊 Resumen de tu uso (30 días)
          </h2>
          
          <div className="space-y-3">
            {/* Paquete */}
            <div className="flex items-center justify-between py-2 border-b border-gray-700">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <div>
                  <span className={`font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    📦 Paquete: {packageName}
                  </span>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Incluye: VPN + 1 Router + 
                    {packageName.includes('HOTSPOT') && ' Hotspot'}
                    {packageName.includes('PPPOE') && (packageName.includes('HOTSPOT') ? ' + PPPoE' : ' PPPoE')}
                  </p>
                </div>
              </div>
              <span className={`font-mono text-lg font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                ${amounts.packagePrice}
              </span>
            </div>
            
            {/* Routers extra */}
            {amounts.extraRouters > 0 && (
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <div>
                    <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      ➕ {amounts.extraRouters} Router{amounts.extraRouters > 1 ? 'es' : ''} adicional{amounts.extraRouters > 1 ? 'es' : ''}
                    </span>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      (${pricingConfig?.extra_router_cost || 50} × {amounts.extraRouters})
                    </p>
                  </div>
                </div>
                <span className={`font-mono ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  ${amounts.extraRoutersCost}
                </span>
              </div>
            )}
            
            {/* Total */}
            <div className={`flex items-center justify-between pt-3 border-t-2 ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
              <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Total Mensual:
              </span>
              <span className="text-2xl font-bold text-blue-600">
                ${licenseData?.pricing?.final_monthly_total || amounts.total}
              </span>
            </div>
          </div>
        </div>
        
        {/* Opciones de Pago */}
        <div className={`rounded-xl p-6 mb-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {licenseData?.billing_cycle ? 'Renovación de tu Plan:' : 'Elige tu forma de pago:'}
          </h3>
          
          {/* Si ya tiene billing_cycle, solo mostrar información */}
          {licenseData?.billing_cycle && (
            <div className="bg-blue-500 bg-opacity-20 border-2 border-blue-500 p-4 rounded-lg mb-4">
              <p className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Tu plan actual: {
                  licenseData.billing_cycle === 'monthly' ? 'Mensual' :
                  licenseData.billing_cycle === 'semiannual' ? 'Semestral' :
                  licenseData.billing_cycle === 'annual' ? 'Anual' : 'Pago Único'
                }
              </p>
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Monto a pagar: <span className="text-2xl font-bold text-blue-600">${amounts.total.toFixed(2)}</span>
              </p>
            </div>
          )}
          
          {/* Solo mostrar opciones si NO tiene billing_cycle (primer pago) */}
          {!licenseData?.billing_cycle && (
          
          <div className="space-y-3">
            {/* Mensual */}
            <label className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all ${
              billingCycle === 'monthly' 
                ? 'bg-blue-500 bg-opacity-20 border-2 border-blue-500' 
                : isDark ? 'bg-gray-700 border border-gray-600' : 'bg-gray-50 border border-gray-300'
            }`}>
              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  name="billing"
                  value="monthly"
                  checked={billingCycle === 'monthly'}
                  onChange={(e) => setBillingCycle(e.target.value)}
                  className="w-5 h-5"
                />
                <div>
                  <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Mensual</p>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Pago cada mes</p>
                </div>
              </div>
              <span className="text-xl font-bold text-blue-600">
                ${amounts.total.toFixed(2)}
              </span>
            </label>
            
            {/* Semestral - OCULTO */}
            {/* 
            <label className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all ${
              billingCycle === 'semiannual' 
                ? 'bg-blue-500 bg-opacity-20 border-2 border-blue-500' 
                : isDark ? 'bg-gray-700 border border-gray-600' : 'bg-gray-50 border border-gray-300'
            }`}>
              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  name="billing"
                  value="semiannual"
                  checked={billingCycle === 'semiannual'}
                  onChange={(e) => setBillingCycle(e.target.value)}
                  className="w-5 h-5"
                />
                <div>
                  <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Semestral</p>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    6 meses - 15% descuento
                  </p>
                  {amounts.saving > 0 && billingCycle === 'semiannual' && (
                    <p className="text-sm text-green-500 font-medium">
                      Ahorras ${amounts.saving.toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
              <span className="text-xl font-bold text-blue-600">
                ${(pricingConfig?.[licenseData?.package_key]?.semiannual || 0).toFixed(2)}
              </span>
            </label>
            */}
            
            {/* Anual - OCULTO */}
            {/*
            <label className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all ${
              billingCycle === 'annual' 
                ? 'bg-blue-500 bg-opacity-20 border-2 border-blue-500' 
                : isDark ? 'bg-gray-700 border border-gray-600' : 'bg-gray-50 border border-gray-300'
            }`}>
              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  name="billing"
                  value="annual"
                  checked={billingCycle === 'annual'}
                  onChange={(e) => setBillingCycle(e.target.value)}
                  className="w-5 h-5"
                />
                <div>
                  <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Anual</p>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    12 meses - 25% descuento
                  </p>
                  {amounts.saving > 0 && billingCycle === 'annual' && (
                    <p className="text-sm text-green-500 font-medium">
                      Ahorras ${amounts.saving.toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
              <span className="text-xl font-bold text-blue-600">
                ${(pricingConfig?.[licenseData?.package_key]?.annual || 0).toFixed(2)}
              </span>
            </label>
            */}
            
            {/* Pago Único */}
            <label className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all ${
              billingCycle === 'lifetime' 
                ? 'bg-blue-500 bg-opacity-20 border-2 border-blue-500' 
                : isDark ? 'bg-gray-700 border border-gray-600' : 'bg-gray-50 border border-gray-300'
            }`}>
              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  name="billing"
                  value="lifetime"
                  checked={billingCycle === 'lifetime'}
                  onChange={(e) => setBillingCycle(e.target.value)}
                  className="w-5 h-5"
                />
                <div>
                  <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Pago Único</p>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Sin pagos futuros - 30% descuento
                  </p>
                  {amounts.saving > 0 && billingCycle === 'lifetime' && (
                    <p className="text-sm text-green-500 font-medium">
                      Ahorras ${amounts.saving.toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
              <span className="text-xl font-bold text-blue-600">
                ${(pricingConfig?.[licenseData?.package_key]?.lifetime || 0).toFixed(2)}
              </span>
            </label>
          </div>
          )}
        </div>
        
        {/* Datos Bancarios */}
        <div className={`rounded-xl p-6 mb-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Datos para Transferencia
          </h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between py-2">
              <span className={`font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Monto a pagar:
              </span>
              <span className="text-2xl font-bold text-blue-600">
                ${billingCycle === 'monthly' ? amounts.total.toFixed(2) : (pricingConfig?.[licenseData?.package_key]?.[billingCycle] || 0).toFixed(2)}
              </span>
            </div>
            <div className="py-2 border-t border-gray-700">
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <span className="font-semibold">Banco:</span> {licenseData?.bank_details?.bank_name || 'N/A'}
              </p>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <span className="font-semibold">Titular:</span> {licenseData?.bank_details?.account_holder || 'N/A'}
              </p>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <span className="font-semibold">Número de cuenta:</span> {licenseData?.bank_details?.account_number || 'N/A'}
              </p>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <span className="font-semibold">CLABE:</span> {licenseData?.bank_details?.clabe || 'N/A'}
              </p>
            </div>
          </div>
        </div>
        
        {/* Upload Comprobante */}
        <div className={`rounded-xl p-6 mb-6 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Subir Comprobante de Pago
          </h3>
          <input
            type="file"
            accept="image/*,.pdf"
            onChange={(e) => setProofFile(e.target.files[0])}
            className={`w-full p-3 rounded-lg border ${
              isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
            }`}
          />
          <textarea
            placeholder="Notas adicionales (opcional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className={`w-full mt-3 p-3 rounded-lg border ${
              isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
            }`}
          />
        </div>
        
        {/* Botón Enviar */}
        <button
          onClick={handleSubmit}
          disabled={submitting || !proofFile}
          className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
            submitting || !proofFile
              ? 'bg-gray-500 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
          } text-white`}
        >
          {submitting ? 'Enviando...' : 'Enviar Solicitud'}
        </button>
      </div>
    </div>
  );
};

export default PaymentSummaryPage;
